import PointerSelection, {
  type PointerSelectionHandle,
} from '@components/ArtBoard/PointerSelection/PointerSelection'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import { useGesture } from '@use-gesture/react'
import type React from 'react'
import { type RefObject, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { AppState } from '@/types'
import { isInsideOwnWheel } from '@/utils'
import { useEventListener } from '../../../hooks'
import {
  useDragEvents,
  usePinchEvents,
  useWheelEvents,
} from '../Canvas/hooks/events'
import { ELEMENT_GESTURE_LISTENER_CONTAINER } from '../Canvas/hooks/events/useDragEvents'
import css from './GestureListener.module.scss'

type GestureListenerProps = {
  movableContainerRef: RefObject<any>
  gestureListenerContainerRef: RefObject<any>
  children?: React.ReactNode
}

const GestureListener: React.FC<GestureListenerProps> = ({
  movableContainerRef,
  gestureListenerContainerRef,
  children,
}) => {
  const dispatch = useDispatch()

  const width = useSelector<AppState, number>(({ canvas }) => canvas.width)

  // a pinch arrives as a ctrl+wheel, and left alone it zooms the whole page;
  // so does Cmd+wheel in Firefox on a Mac, and it zooms the canvas (OCT-1834)
  const wheel = (e: WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey && isInsideOwnWheel(e.target)) {
      return
    }

    e.preventDefault()
  }

  const onResize = () => {
    if (
      movableContainerRef &&
      movableContainerRef.current &&
      width !== movableContainerRef.current.clientWidth
    ) {
      dispatch({
        type: CanvasActionTypes.SET_WIDTH,
        payload: {
          width: movableContainerRef.current.clientWidth,
        },
      })
    }
  }

  useEventListener('resize', onResize, movableContainerRef)

  useEffect(() => {
    if (gestureListenerContainerRef && gestureListenerContainerRef.current) {
      gestureListenerContainerRef.current.addEventListener('wheel', wheel)
    }
    onResize()

    return () => {
      if (gestureListenerContainerRef && gestureListenerContainerRef.current) {
        gestureListenerContainerRef.current.removeEventListener('wheel', wheel)
      }
    }
  }, [])

  const setPointerSelectionRectRef = useRef<PointerSelectionHandle>(null)
  const dragEvents = useDragEvents(
    setPointerSelectionRectRef.current?.onChangeSelection
  )
  const wheelEvents = useWheelEvents()
  const pinchEvents = usePinchEvents()

  useGesture(
    {
      onDragStart: (event) =>
        dragEvents.onDragStart(event, movableContainerRef),
      onDrag: dragEvents.onDrag,
      onDragEnd: (event) => dragEvents.onDragEnd(event, movableContainerRef),
      onWheel: (event) => wheelEvents.onWheel(event, movableContainerRef),
      onPinch: pinchEvents.onPinch,
    },
    {
      target: gestureListenerContainerRef,
      drag: {
        pointer: {
          buttons: [1, 4],
        },
      },
    }
  )

  return (
    <div
      className={css.container}
      id={ELEMENT_GESTURE_LISTENER_CONTAINER}
      ref={gestureListenerContainerRef}
    >
      <PointerSelection ref={setPointerSelectionRectRef} />
      {children}
    </div>
  )
}

export default GestureListener
